import { GetRowIdParams, GridReadyEvent, RowClassRules } from 'ag-grid-community';
import { ClientSideBlotterGrid } from 'components/shared/Blotter/Grid/client-side-blotter';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { useUserNotification } from 'hooks/useUserNotification';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import NoRowsOverlay from 'routes/secondary-market-batch/grid/components/no-rows-overlay';
import {
  processCellForClipboard,
  useProcessCellFromClipboard,
} from 'routes/secondary-market-batch/grid/hooks/clipboard';
import { useBatchUpdate } from 'routes/secondary-market-batch/grid/hooks/use-batch-update';
import { useColumnTypesByOrderType } from 'routes/secondary-market-batch/grid/hooks/use-column-types';
import { useFillHandle } from 'routes/secondary-market-batch/grid/hooks/use-fill-hande';

import './index.css';

import { useGridApiContext } from 'routes/secondary-market-batch/grid/hooks/use-grid-api-context';
import {
  useGridStateByOrderType,
  usePreferenceByOrderType,
} from 'routes/secondary-market-batch/grid/hooks/use-grid-state';
import { usePasteSummary } from 'routes/secondary-market-batch/grid/hooks/use-paste-summary';
import { useSelection } from 'routes/secondary-market-batch/grid/hooks/use-selection';
import { useOrderType } from 'routes/secondary-market-batch/hooks/use-order-type';
import { RowData } from 'state/workspaces/secondary-market/batch/model';

import { useColumnDefinitions } from '../hooks/use-column-definitions';
const defaultColumnWidth = { width: 200 };

export const getRowId = (row: RowData) => row.rowId;

const getRowIdCallback = (params: GetRowIdParams<RowData>) => getRowId(params.data);

const rowClassRules: RowClassRules<RowData> = {
  'bg-primary bg-opacity-25': params => params.data?.modifyStatus === 'loading',
};
export function Grid() {
  const { orderType } = useOrderType();
  const gridApiContext = useGridApiContext();

  const notification = useUserNotification();
  const { formatMessage } = useIntl();

  const {
    state: pasteSummaryState,
    startPaste,
    endPaste,
    registerPendingCellPaste,
    registerSuccessfulCellPaste,
    registerFailedCellPaste,
    resetPasteSummaryState,
  } = usePasteSummary();

  const handleGridReady = useCallback(
    (e: GridReadyEvent) => {
      gridApiContext?.setApi(e.api);
    },
    [gridApiContext],
  );
  const { data, status } = useGridStateByOrderType(orderType);
  const columnDefinitions = useColumnDefinitions(orderType);
  const { columnState, columnGroupState, onPreferenceChanged } = usePreferenceByOrderType(orderType);
  const columnTypes = useColumnTypesByOrderType(orderType);
  const { onCellValueChanged } = useBatchUpdate(orderType);
  const name = `secondary-market-batch-grid.${orderType}`;
  const getHeaderIntlKey = useMemo(() => getHeaderForBlotterIntlKey(name), [name]);
  const { handleSelectionChanged } = useSelection(orderType, gridApiContext?.api ?? undefined);
  const defaultColDef = useDefaultColumnDefinition<RowData>(getHeaderIntlKey, defaultColumnWidth);

  const context = useMemo(() => ({ orderType }), [orderType]);
  const cellSelection = useFillHandle();
  const pasteCallbacks = useMemo(
    () => ({
      onCellPastePending: registerPendingCellPaste,
      onCellPasteSuccess: registerSuccessfulCellPaste,
      onCellPasteFailure: registerFailedCellPaste,
    }),
    [registerFailedCellPaste, registerPendingCellPaste, registerSuccessfulCellPaste],
  );

  const processClipboard = useProcessCellFromClipboard(orderType, getRowId, pasteCallbacks);

  useEffect(() => {
    const hasCompletedPaste =
      pasteSummaryState.hasPasteEnded &&
      pasteSummaryState.pendingCells === 0 &&
      pasteSummaryState.successfulCells + pasteSummaryState.failedCells > 0;

    if (hasCompletedPaste) {
      const content = formatMessage(
        { id: 'secondaryMarket.batch.paste.summary.error' },
        {
          successfulCells: pasteSummaryState.successfulCells,
          failedCells: pasteSummaryState.failedCells,
        },
      );

      if (pasteSummaryState.failedCells > 0) {
        notification.warn({
          title: { id: 'toast.paste' },
          content: [content],
        });
      } else {
        notification.success({
          title: { id: 'toast.paste' },
          content: [content],
        });
      }

      resetPasteSummaryState();
    }
  }, [notification, pasteSummaryState, formatMessage, resetPasteSummaryState]);

  return (
    <ClientSideBlotterGrid<RowData>
      key={orderType}
      onGridReady={handleGridReady}
      loading={status === 'loading'}
      name={name}
      items={data}
      context={context}
      columnTypes={columnTypes}
      defaultColDef={defaultColDef}
      columnDefinitions={columnDefinitions}
      columnGroupState={columnGroupState}
      columnState={columnState}
      onBlotterColumnStateChanged={onPreferenceChanged}
      logAnalyticName="Secondary market"
      getRowId={getRowIdCallback}
      rowSelection={{ mode: 'multiRow', checkboxes: true, selectAll: 'filtered' }}
      selectionColumnDef={{ pinned: true }}
      onSelectionChanged={handleSelectionChanged}
      onPasteStart={startPaste}
      onPasteEnd={endPaste}
      processCellForClipboard={processCellForClipboard}
      processCellFromClipboard={processClipboard}
      rowClassRules={rowClassRules}
      enableBrowserTooltips={false}
      tooltipShowDelay={0}
      onCellValueChanged={onCellValueChanged}
      enterNavigatesVerticallyAfterEdit
      cellSelection={cellSelection}
      noRowsOverlayComponent={NoRowsOverlay}
    >
      {status === 'error' && (
        <em
          className="icon text-danger  icon-md bg-opacity-25"
          title="Grid loading error"
          aria-label="Grid loading error"
        >
          error
        </em>
      )}
    </ClientSideBlotterGrid>
  );
}
